/**
 * Cross-links the main Catchball loop surfaces for a cycle: composer, proposal inbox, workbench, loops list.
 * Resolves workbench URL to the active (non-closed) loop when possible.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, LayoutDashboard, ListTree, CheckSquare2 } from 'lucide-react';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import type { CatchballLoop } from '../../../types/prioritization/catchballLoop';

export type CatchballLoopSubnavActive = 'composer' | 'inbox' | 'workbench' | 'dashboard' | 'setup' | 'contribute' | 'intelligence' | 'reconcile';

function resolveWorkbenchLoopId(cycleId: string, loops: CatchballLoop[]): string | null {
  const cycleLoops = loops
    .filter((l) => l.cycleId === cycleId)
    .sort((a, b) => b.loopNumber - a.loopNumber);
  const open = cycleLoops.find((l) => l.state !== 'closed');
  return open?.id ?? cycleLoops[0]?.id ?? null;
}

type SubnavVariant = 'default' | 'compact' | 'embedded';

function tabClass(active: boolean, variant: SubnavVariant = 'default') {
  if (variant === 'embedded') {
    return `inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-primary-600 text-primary-700 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/20'
        : 'border-transparent text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] hover:bg-[var(--surface)]/80 dark:hover:bg-[var(--hover-tint)]'
    }`;
  }
  if (variant === 'compact') {
    return `inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5  transition-colors ${
      active
        ? 'bg-[var(--hover-tint)] text-[var(--text-pri)] dark:bg-[var(--hover-tint)] dark:text-[var(--text-pri)]'
        : 'text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-pri)]'
    }`;
  }
  return `inline-flex items-center gap-1.5  px-3 py-2 text-sm font-medium transition-colors ${
    active
      ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] ring-1 ring-indigo-200 dark:bg-[var(--slate)]/40 dark:ring-indigo-800/60'
      : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-pri)]'
  }`;
}

function navClass(variant: SubnavVariant) {
  if (variant === 'embedded') {
    return 'flex items-center gap-0 overflow-x-auto border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-2';
  }
  if (variant === 'compact') {
    return 'flex flex-wrap items-center gap-1';
  }
  return 'flex flex-wrap items-center gap-2 border-b border-[var(--rule)] pb-3 dark:border-[var(--rule)]';
}

export function CatchballLoopSubnav({
  cycleId,
  active,
  workbenchLoopId,
  compact,
  embedded,
  onNavigate,
}: {
  cycleId: string;
  active: CatchballLoopSubnavActive;
  /** When on workbench, pass current loop id so the tab targets this loop. */
  workbenchLoopId?: string | null;
  /** Compact style for use in sticky headers (smaller text, no bottom border). */
  compact?: boolean;
  /** Embedded in cycle detail — matches primary tab bar weight and contrast. */
  embedded?: boolean;
  /**
   * When provided, the subnav renders <button> elements instead of <Link>.
   * The callback receives the target view and, for 'workbench', the resolved loopId.
   * Use this to keep the cycle header frozen while switching catchball sub-views inline.
   */
  onNavigate?: (view: CatchballLoopSubnavActive, loopId?: string) => void;
}) {
  const variant: SubnavVariant = embedded ? 'embedded' : compact ? 'compact' : 'default';
  const loops = useCatchballLoopStore((s) => s.loops);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    void fetchLoops(cycleId);
  }, [cycleId, fetchLoops]);

  const workbenchTargetId = useMemo(() => {
    if (workbenchLoopId) return workbenchLoopId;
    return resolveWorkbenchLoopId(cycleId, loops);
  }, [cycleId, loops, workbenchLoopId]);

  const iconSize = variant === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  // When onNavigate is provided, use buttons so the parent can render content inline
  // without triggering a full-page route change (cycle header stays frozen).
  // DEP-025: Catchball replaces Request/Workspace as peer tabs. Retained call sites
  // outside Cycle Exchange only see Balls + Inbox (+ reconcile when active).
  if (onNavigate) {
    return (
      <nav className={navClass(variant)} aria-label="Catchball views for this cycle">
        <button type="button" onClick={() => onNavigate('dashboard')} className={tabClass(active === 'dashboard', variant)}>
          <ListTree className={`${iconSize} shrink-0 opacity-80`} />
          Balls
        </button>
        <button type="button" onClick={() => onNavigate('inbox')} className={tabClass(active === 'inbox', variant)}>
          <Inbox className={`${iconSize} shrink-0 opacity-80`} />
          Inbox
        </button>
        {active === 'reconcile' && (
          <button type="button" onClick={() => onNavigate('reconcile')} className={tabClass(true, variant)}>
            <CheckSquare2 className={`${iconSize} shrink-0 opacity-80`} />
            Reconciliation
          </button>
        )}
      </nav>
    );
  }

  const inboxPath = workbenchTargetId
    ? `/prioritization/cascade/catchball/${cycleId}/${workbenchTargetId}`
    : null;

  return (
    <nav className={navClass(variant)} aria-label="Catchball views for this cycle">
      <Link
        to={`/prioritization/cascade/dashboard/${cycleId}`}
        className={tabClass(active === 'dashboard' || active === 'workbench', variant)}
      >
        <ListTree className={`${iconSize} shrink-0 opacity-80`} />
        Balls
      </Link>
      {/* Named for the page it opens, which titles itself "Proposal Review".
          It read "Inbox" while the page said Proposal Review and the ball
          screen's action strip said Proposal review — one destination, three
          names. */}
      <Link to={`/prioritization/cascade/inbox/${cycleId}`} className={tabClass(active === 'inbox', variant)}>
        <Inbox className={`${iconSize} shrink-0 opacity-80`} />
        Proposal review
      </Link>
      {/* The ball you are holding. Named for what it is: this tab used to read
          "Inbox" too, so the bar showed two tabs with the same word pointing at
          different screens (the proposal queue vs this loop's desk). */}
      {inboxPath && active === 'workbench' && (
        <Link to={inboxPath} className={tabClass(true, variant)}>
          <LayoutDashboard className={`${iconSize} shrink-0 opacity-80`} />
          This ball
        </Link>
      )}
      {active === 'reconcile' && (
        <Link
          to={`/prioritization/cascade/reconcile/${cycleId}`}
          className={tabClass(true, variant)}
        >
          <CheckSquare2 className={`${iconSize} shrink-0 opacity-80`} />
          Reconciliation
        </Link>
      )}
    </nav>
  );
}
